import { useEffect, useRef } from "react";
import type { ClaimState } from "../store/VnvAssistStore";

const HIGHLIGHT_NAMES = {
  claim: "vnv-claim",
  active: "vnv-claim-active",
} as const;

/**
 * A chunk of the text layer's flat representation tied to a DOM ``Text`` node.
 *
 * ``rawOffsets`` is a parallel array to the normalized contribution this chunk
 * makes to the flat string — entry ``i`` is the raw character index within
 * ``node`` that produced the ``i``-th normalized character. This lets us map
 * an offset in the flat string back to ``(node, rawIndex)`` when building
 * Ranges, even when whitespace normalization compresses multiple raw chars
 * into a single flat char.
 */
type TextChunk = {
  node: Text;
  startOffset: number;
  rawOffsets: number[];
};

/**
 * Flatten every text node under ``root`` into a single lowercase, whitespace-
 * normalized string. Each raw whitespace run collapses to a single space and
 * adjacent spans get an inter-span space when neither side already has
 * whitespace — pdfjs text-layer spans don't consistently carry trailing
 * spaces, so without this "hello" + "world" would flatten to "helloworld".
 */
function flattenTextLayer(root: Node): {
  flat: string;
  chunks: TextChunk[];
} {
  const chunks: TextChunk[] = [];
  let flat = "";

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
  let node: Node | null;
  while ((node = walker.nextNode())) {
    const raw = node.textContent ?? "";
    if (!raw) continue;

    const lower = raw.toLowerCase();
    const rawOffsets: number[] = [];
    let normalized = "";
    let lastWasSpace = false;
    for (let i = 0; i < lower.length; i++) {
      const ch = lower[i];
      const isSpace = /\s/.test(ch);
      if (isSpace) {
        if (!lastWasSpace && normalized.length > 0) {
          normalized += " ";
          rawOffsets.push(i);
          lastWasSpace = true;
        }
      } else {
        normalized += ch;
        rawOffsets.push(i);
        lastWasSpace = false;
      }
    }
    if (!normalized) continue;

    if (flat.length > 0 && !flat.endsWith(" ") && !normalized.startsWith(" ")) {
      flat += " ";
    }

    chunks.push({
      node: node as Text,
      startOffset: flat.length,
      rawOffsets,
    });
    flat += normalized;
  }

  return { flat, chunks };
}

/**
 * Build a Range that covers ``[flatStart, flatEnd)`` in the flat string by
 * mapping those offsets back to ``(Text node, rawIndex)`` pairs via the
 * chunk ``rawOffsets`` arrays. Returns ``null`` if the offsets fall on an
 * inter-span space (not owned by any chunk) or if Range construction fails.
 */
function buildRange(
  chunks: TextChunk[],
  flatStart: number,
  flatEnd: number,
): Range | null {
  if (flatStart >= flatEnd) return null;

  let startChunk: TextChunk | null = null;
  let endChunk: TextChunk | null = null;
  for (const chunk of chunks) {
    const chunkStart = chunk.startOffset;
    const chunkEnd = chunk.startOffset + chunk.rawOffsets.length;
    if (
      startChunk === null &&
      flatStart >= chunkStart &&
      flatStart < chunkEnd
    ) {
      startChunk = chunk;
    }
    if (flatEnd > chunkStart && flatEnd <= chunkEnd) {
      endChunk = chunk;
      break;
    }
  }

  if (!startChunk || !endChunk) return null;

  const startRawIdx = startChunk.rawOffsets[flatStart - startChunk.startOffset];
  const lastCharRawIdx =
    endChunk.rawOffsets[flatEnd - 1 - endChunk.startOffset];
  if (startRawIdx === undefined || lastCharRawIdx === undefined) return null;
  const endRawIdx = lastCharRawIdx + 1;

  try {
    const range = new Range();
    range.setStart(startChunk.node, startRawIdx);
    range.setEnd(
      endChunk.node,
      Math.min(endRawIdx, (endChunk.node.textContent ?? "").length),
    );
    return range;
  } catch {
    return null;
  }
}

/**
 * Multi-claim CSS Custom Highlight API hook.
 *
 * ``::highlight(vnv-claim)`` and ``::highlight(vnv-claim-active)`` are defined
 * globally in ``scss/_custom.scss``; this hook only registers Range objects.
 *
 * A single 100ms ``setTimeout`` gives react-pdf's text layer time to populate.
 * The flatten-then-search strategy is essential: a 100+ character claim
 * routinely crosses pdfjs ``<span>`` boundaries, which a per-node ``indexOf``
 * walk would miss entirely.
 */
export function useMultiClaimHighlight(
  containerRef: React.RefObject<HTMLDivElement>,
  claims: ClaimState[],
  activeClaim: ClaimState | null,
  pageNumber: number,
) {
  const scrolledActiveRef = useRef<number | null>(null);

  useEffect(() => {
    if (typeof CSS === "undefined" || !CSS.highlights) return;

    Object.values(HIGHLIGHT_NAMES).forEach((name) =>
      CSS.highlights.delete(name),
    );

    if (!containerRef.current) return;

    const timeoutId = setTimeout(() => {
      const container = containerRef.current;
      if (!container) return;
      const textLayer = container.querySelector(
        ".react-pdf__Page__textContent",
      );
      if (!textLayer) return;

      const { flat, chunks } = flattenTextLayer(textLayer);
      if (!flat || chunks.length === 0) return;

      const buckets: Record<keyof typeof HIGHLIGHT_NAMES, Range[]> = {
        claim: [],
        active: [],
      };

      const normalizeNeedle = (t: string) =>
        t.toLowerCase().replace(/\s+/g, " ").trim().slice(0, 120);

      type Needle = { text: string; bucket: Range[] };
      const needles: Needle[] = [];

      for (const claim of claims) {
        if (
          claim.page_number !== null &&
          claim.page_number !== undefined &&
          claim.page_number !== pageNumber
        ) {
          continue;
        }
        const needle = normalizeNeedle(claim.text);
        if (needle) needles.push({ text: needle, bucket: buckets.claim });
      }

      if (activeClaim) {
        const needle = normalizeNeedle(activeClaim.text);
        if (needle) needles.push({ text: needle, bucket: buckets.active });
      }

      const scrollNeedle =
        activeClaim && scrolledActiveRef.current !== activeClaim.id
          ? normalizeNeedle(activeClaim.text).slice(0, 80) || null
          : null;

      for (const { text: needle, bucket } of needles) {
        let idx = 0;
        while ((idx = flat.indexOf(needle, idx)) !== -1) {
          const range = buildRange(chunks, idx, idx + needle.length);
          if (range) bucket.push(range);
          idx += needle.length;
        }
      }

      let scrollRange: Range | null = null;
      if (scrollNeedle) {
        const idx = flat.indexOf(scrollNeedle);
        if (idx !== -1) {
          scrollRange = buildRange(chunks, idx, idx + scrollNeedle.length);
        }
      }

      for (const [key, ranges] of Object.entries(buckets) as [
        keyof typeof HIGHLIGHT_NAMES,
        Range[],
      ][]) {
        if (ranges.length > 0) {
          CSS.highlights.set(HIGHLIGHT_NAMES[key], new Highlight(...ranges));
        }
      }

      if (activeClaim && scrollRange) {
        scrolledActiveRef.current = activeClaim.id;
        const anchor =
          scrollRange.startContainer.parentElement ||
          (scrollRange.startContainer as unknown as Element);
        anchor?.scrollIntoView?.({ behavior: "smooth", block: "center" });
      }
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      if (typeof CSS !== "undefined" && CSS.highlights) {
        Object.values(HIGHLIGHT_NAMES).forEach((name) =>
          CSS.highlights.delete(name),
        );
      }
    };
  }, [claims, activeClaim, pageNumber, containerRef]);
}
